import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Presentation, Save, Upload, Trash2, ImageIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import { DiaVolgordeKaart, GridEditorKaart, CustomDiasKaart } from "@/components/PresentatieSlideConfig";

const MERKKLEUREN = [
  { k: "primair", label: "Primair", uitleg: "titels & koppen" },
  { k: "secundair", label: "Secundair", uitleg: "subtitels & labels" },
  { k: "accent", label: "Accent", uitleg: "prijzen & highlights" },
  { k: "tekst", label: "Tekst", uitleg: "lopende tekst" },
];

function BestandKaart({ soort, titel, uitleg, rec, isAdmin, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/instellingen/presentatie/bestanden/${soort}`, fd);
      onChanged(data);
      toast.success(`${titel} geüpload`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload mislukt");
    } finally {
      setBusy(false);
    }
  };
  const verwijder = async () => {
    try {
      const { data } = await api.delete(`/instellingen/presentatie/bestanden/${soort}/${rec.id}`);
      onChanged(data);
      toast.success(`${titel} verwijderd`);
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid={`presentatie-${soort}-kaart`}>
      <h2 className="font-display text-xl font-medium">{titel}</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">{uitleg}</p>
      <div className="flex items-center gap-4">
        <div className="h-24 w-40 rounded-lg border border-border bg-secondary flex items-center justify-center overflow-hidden shrink-0">
          {rec ? (
            <img src={`${API_BASE}/instellingen/presentatie/bestanden/${soort}/${rec.id}`}
              alt={titel} className="max-h-full max-w-full object-contain" />
          ) : (
            <ImageIcon className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        {isAdmin && (
          <div className="flex flex-col gap-2">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" disabled={busy}
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
                data-testid={`presentatie-${soort}-upload`} />
              <span className="inline-flex items-center h-9 px-3 rounded-md border border-border bg-background text-sm hover:bg-secondary">
                <Upload className="h-4 w-4 mr-2" />{busy ? "Uploaden…" : rec ? "Vervangen" : "Uploaden"}
              </span>
            </label>
            {rec && (
              <Button variant="outline" size="sm" onClick={verwijder}
                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                data-testid={`presentatie-${soort}-verwijder`}>
                <Trash2 className="h-4 w-4 mr-1" /> Verwijderen
              </Button>
            )}
          </div>
        )}
      </div>
      {rec && <p className="text-xs text-muted-foreground mt-2">{rec.naam}</p>}
    </div>
  );
}

function KleurVeld({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#FFFFFF"} disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 rounded border border-border cursor-pointer bg-background"
          data-testid={`${testid}-picker`} />
        <Input value={value || ""} disabled={disabled} className="w-28 font-mono"
          onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </div>
    </div>
  );
}

export default function InstellingenPresentaties() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/instellingen/presentatie").then((r) => setCfg(r.data)).catch(() => toast.error("Laden mislukt"));
  }, []);

  const save = async (payload, melding) => {
    setSaving(true);
    try {
      const { data } = await api.put("/instellingen/presentatie", payload);
      setCfg(data);
      toast.success(melding);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const setBedrag = (i, veld, v) => setCfg((c) => {
    const rows = [...(c.min_bestelbedragen || [])];
    rows[i] = { ...rows[i], [veld]: v };
    return { ...c, min_bestelbedragen: rows };
  });

  if (!cfg) return <div className="text-muted-foreground">Laden…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="instellingen-presentaties">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-lg bg-secondary flex items-center justify-center"><Presentation className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Presentaties</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configurator voor de assortimentspresentaties (PPTX) die je vanaf een organisatiepagina genereert.
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6" data-testid="presentatie-kleuren-kaart">
        <h2 className="font-display text-xl font-medium">Merkkleuren</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Je huisstijlkleuren worden automatisch op de juiste content toegepast: primair op titels en koppen,
          secundair op subtitels en labels, accent op prijzen en highlights, en de tekstkleur op de lopende tekst.
        </p>
        <div className="flex flex-wrap items-end gap-6">
          {MERKKLEUREN.map(({ k, label, uitleg }) => (
            <div key={k}>
              <KleurVeld label={`${label} — ${uitleg}`} value={cfg.merkkleuren?.[k]} disabled={!isAdmin}
                testid={`presentatie-merkkleur-${k}`}
                onChange={(v) => setCfg((c) => ({ ...c, merkkleuren: { ...c.merkkleuren, [k]: v } }))} />
            </div>
          ))}
        </div>
        {isAdmin && (
          <div className="mt-5 flex justify-end">
            <Button disabled={saving} data-testid="presentatie-kleuren-save"
              onClick={() => save({ merkkleuren: cfg.merkkleuren }, "Merkkleuren opgeslagen")}>
              <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Opslaan"}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-6" data-testid="presentatie-overons-kaart">
        <h2 className="font-display text-xl font-medium">"Over ons"-tekst</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Vaste bedrijfstekst voor de tweede dia. Wordt bij een andere taal automatisch vertaald met AI.
        </p>
        <Textarea rows={5} value={cfg.over_ons || ""} disabled={!isAdmin}
          placeholder="Korte bedrijfsomschrijving…"
          onChange={(e) => setCfg((c) => ({ ...c, over_ons: e.target.value }))}
          data-testid="presentatie-over-ons" />
        {isAdmin && (
          <div className="mt-4 flex justify-end">
            <Button disabled={saving} data-testid="presentatie-overons-save"
              onClick={() => save({ over_ons: cfg.over_ons }, "\"Over ons\" opgeslagen")}>
              <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Opslaan"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <BestandKaart soort="logo" titel="Logo" isAdmin={isAdmin} rec={cfg.logo} onChanged={setCfg}
          uitleg="Eigen logo voor de presentaties. Zonder upload wordt het standaard NextProduct-logo gebruikt." />
        <BestandKaart soort="cover" titel="Cover-afbeelding" isAdmin={isAdmin} rec={cfg.cover} onChanged={setCfg}
          uitleg="Volledige achtergrondafbeelding op de eerste dia, met de titel in een balk in de accentkleur." />
      </div>

      <div className="bg-card border border-border rounded-xl p-6" data-testid="presentatie-minbestel-kaart">
        <h2 className="font-display text-xl font-medium">Minimum bestelbedrag per land</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Wordt onderaan de besteltabel getoond, op basis van het land van de klant (verzendadres, anders hoofdadres).
        </p>
        <div className="space-y-2">
          {(cfg.min_bestelbedragen || []).map((r, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`presentatie-minbestel-rij-${i}`}>
              <Input className="w-48" placeholder="Land (bijv. Nederland)" value={r.land || ""} disabled={!isAdmin}
                onChange={(e) => setBedrag(i, "land", e.target.value)} data-testid={`presentatie-minbestel-land-${i}`} />
              <div className="relative">
                <span className="absolute left-3 top-1.5 text-sm text-muted-foreground">€</span>
                <Input type="number" min="0" step="0.01" className="w-32 pl-7" value={r.bedrag ?? ""} disabled={!isAdmin}
                  onChange={(e) => setBedrag(i, "bedrag", e.target.value)} data-testid={`presentatie-minbestel-bedrag-${i}`} />
              </div>
              {isAdmin && (
                <button onClick={() => setCfg((c) => ({ ...c, min_bestelbedragen: c.min_bestelbedragen.filter((_, j) => j !== i) }))}
                  className="h-9 w-9 rounded-md border border-border flex items-center justify-center hover:bg-destructive/10"
                  data-testid={`presentatie-minbestel-verwijder-${i}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </button>
              )}
            </div>
          ))}
          {(cfg.min_bestelbedragen || []).length === 0 && (
            <div className="text-sm text-muted-foreground">Nog geen landen toegevoegd.</div>
          )}
        </div>
        {isAdmin && (
          <div className="mt-4 flex items-center justify-between">
            <Button variant="outline" size="sm" data-testid="presentatie-minbestel-toevoegen"
              onClick={() => setCfg((c) => ({ ...c, min_bestelbedragen: [...(c.min_bestelbedragen || []), { land: "", bedrag: "" }] }))}>
              <Plus className="h-4 w-4 mr-1" /> Land toevoegen
            </Button>
            <Button disabled={saving} data-testid="presentatie-minbestel-save"
              onClick={() => save({ min_bestelbedragen: cfg.min_bestelbedragen }, "Minimum bestelbedragen opgeslagen")}>
              <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Opslaan"}
            </Button>
          </div>
        )}
      </div>

      <CustomDiasKaart cfg={cfg} isAdmin={isAdmin} onSaved={setCfg} />

      <DiaVolgordeKaart cfg={cfg} isAdmin={isAdmin} onSaved={setCfg} />

      <GridEditorKaart cfg={cfg} isAdmin={isAdmin} onSaved={setCfg} />
      {!isAdmin && <p className="text-xs text-muted-foreground">Alleen beheerders kunnen deze instellingen wijzigen.</p>}
    </div>
  );
}
